/**
 * Vietnamese tax codes (mã số thuế, MST) and CCCD — the one rule every form uses, the same as
 * the server's `identity_subjects::vn::normalize_tax_code` (owner decision 2026-09-29):
 *
 * - individual (cá nhân): the MST is the CCCD, 12 digits — there is no separate tax code field;
 * - household business (hộ kinh doanh): the MST is the owner's CCCD, 12 digits;
 * - enterprise / organisation: 10 digits, or 10-3 for a branch; 13 digits typed without the
 *   hyphen are accepted and become 10-3.
 *
 * Pure functions (no React): `npm test` covers them. Used by the customer form and the subject
 * form.
 */

export type TaxpayerKind = 'INDIVIDUAL' | 'HOUSEHOLD_BUSINESS' | 'ORGANIZATION';

/** Refusal codes, as the server sends them. */
export type TaxCodeError = 'TAX_CODE_CCCD_FORMAT' | 'TAX_CODE_FORMAT' | 'TAX_CODE_ANY_FORMAT';

export type TaxCodeResult = { ok: true; value: string } | { ok: false; code: TaxCodeError };

/** The Vietnamese messages of the refusals (also in the i18n bundles). */
export const TAX_CODE_MESSAGES_VI: Record<TaxCodeError, string> = {
  TAX_CODE_CCCD_FORMAT: 'Mã số thuế của cá nhân / hộ kinh doanh là số CCCD (của chủ hộ), gồm đúng 12 chữ số',
  TAX_CODE_FORMAT: 'Mã số thuế doanh nghiệp / tổ chức gồm 10 chữ số, hoặc 10-3 chữ số với chi nhánh (VD 0312345678-001)',
  TAX_CODE_ANY_FORMAT: 'Mã số thuế: 10 hoặc 10-3 chữ số (doanh nghiệp), hoặc số CCCD 12 chữ số (cá nhân, hộ kinh doanh)',
};

const compact = (v: string) => v.replace(/\s+/g, '');

/** Checks and normalises an MST for `kind` (spaces ignored; 13 digits become 10-3). */
export function normalizeTaxCode(kind: TaxpayerKind, value: string): TaxCodeResult {
  const v = compact(value);
  if (kind !== 'ORGANIZATION') return /^\d{12}$/.test(v) ? { ok: true, value: v } : { ok: false, code: 'TAX_CODE_CCCD_FORMAT' };
  if (/^\d{10}(-\d{3})?$/.test(v)) return { ok: true, value: v };
  if (/^\d{13}$/.test(v)) return { ok: true, value: `${v.slice(0, 10)}-${v.slice(10)}` };
  return { ok: false, code: 'TAX_CODE_FORMAT' };
}

/** An invoice buyer's MST: an organisation's (10 / 10-3 / 13 digits) or a CCCD (12 digits). */
export function normalizeInvoiceTaxCode(value: string): TaxCodeResult {
  const org = normalizeTaxCode('ORGANIZATION', value);
  if (org.ok) return org;
  const person = normalizeTaxCode('INDIVIDUAL', value);
  return person.ok ? person : { ok: false, code: 'TAX_CODE_ANY_FORMAT' };
}

/** A 12-digit CCCD: province (3), gender / century digit 0–5, year (2), serial (6). */
export function cccdOk(v: string): boolean {
  const c = compact(v);
  return /^\d{12}$/.test(c) && Number(c[3]) <= 5;
}

/**
 * A CCCD agreeing with the holder's gender and birth year (when known), as the server checks:
 * the refusal codes `CCCD_FORMAT`, `CCCD_GENDER_MISMATCH`, `CCCD_BIRTH_YEAR_MISMATCH`.
 */
export function checkCccd(value: string, birthDate?: string | null, gender?: 'Male' | 'Female' | '' | null): string[] {
  const c = compact(value);
  if (!cccdOk(c)) return ['CCCD_FORMAT'];
  const digit = Number(c[3]);
  const out: string[] = [];
  if (gender && (gender === 'Female') !== (digit % 2 === 1)) out.push('CCCD_GENDER_MISMATCH');
  const year = birthDate ? Number(birthDate.slice(0, 4)) : NaN;
  if (Number.isFinite(year) && year !== 1900 + 100 * Math.floor(digit / 2) + Number(c.slice(4, 6))) out.push('CCCD_BIRTH_YEAR_MISMATCH');
  return out;
}
